// App
import type React from 'react';
import isNil from 'lodash/isNil';

// Constellation
import { Anchor, Text } from '@zillow/constellation';

// Components
import LazyLoad from '../LazyLoad';

// Misc / utils
import { parser as comparablesParser } from '../utils/comparablesUtils';
import { pluralize } from '../utils/stringUtils';
import { useAppSelector } from '../flux/configureStore';
import * as S from './styles';

// Types
import type ComparablesModelType from '../models/Comparables';
type ZestimateType = { rentZestimate: number };
type CompsCarouselCardPropTypes = {
  listing: ComparablesModelType & Partial<ZestimateType>;
  isHovered?: boolean;
  lockHover?: boolean;
  isStriped: boolean;
};

const MAX_CHARS_PER_LINE = 21;

const CompsCarouselCardComponent = ({ listing, isHovered, lockHover, isStriped }: CompsCarouselCardPropTypes) => {
  const longestAddress = useAppSelector((state) => comparablesParser.getLongestAddress(state.comparables.items));

  const propertyPhoto =
    listing && listing.propertyPhotos && listing.propertyPhotos[0] && listing.propertyPhotos[0].medium;
  const listingPriceSqft = isNil(listing.pricePerSqft) ? '—' : `${listing.pricePerSqft} per sq ft`;

  const StandardHeader = (
    <S.CardHeader>
      <S.CardThumbContainer>
        {propertyPhoto ? (
          <>
            {listing.isActive ? (
              <Anchor href={listing.url} target={'_blank'}>
                <LazyLoad>
                  <S.CardThumbnail src={propertyPhoto} alt={`Photo of ${listing.street}`} />
                </LazyLoad>
              </Anchor>
            ) : (
              <LazyLoad>
                <S.CardThumbnail src={propertyPhoto} alt={`Photo of ${listing.street}`} />
              </LazyLoad>
            )}
          </>
        ) : (
          <S.Fallback fontColor="gray100" alt={`Photo of ${listing.street} is not available`} />
        )}
      </S.CardThumbContainer>
      <S.CardPriceRow>
        <Text fontType="body" marginTop="xs" marginLeft="xs">
          <strong>{listing.monthlyRent}/mo</strong>
        </Text>
        <Text fontType="body" marginBottom="xs" marginLeft="xs">
          {listingPriceSqft}
        </Text>
      </S.CardPriceRow>
    </S.CardHeader>
  );

  const isActive = listing.isActive;
  const listingBeds = isNil(listing.beds) ? '—' : comparablesParser.beds(listing.beds);
  const listingBaths = isNil(listing.baths) ? '—' : pluralize('bath', listing.baths, true);
  const listingSqft = isNil(listing.sqft) ? '—' : `${listing.sqft} sq ft`;
  const listingLaundryAmenity = isNil(listing.laundry) ? '—' : listing.laundry;
  const listingParkingAmenity = isNil(listing.parking) ? '—' : listing.parking;
  const listingAcAmenity = isNil(listing.ac) ? '—' : listing.ac;
  const listingHeatingAmenity = isNil(listing.heating) ? '—' : listing.heating;
  const listingPetsAmenity = isNil(listing.pets) ? '—' : listing.pets;

  return (
    <S.CardContainer isHovered={!!(isHovered || lockHover)} isStriped={isStriped}>
      {StandardHeader}
      <S.Divider marginY="xs" />

      <S.CardAddress height={longestAddress.length > MAX_CHARS_PER_LINE ? 60 : null}>
        {isActive ? (
          <Text fontType="bodySmall" fontColor="brand" marginLeft="xs" as="strong">
            <Anchor
              as="a"
              href={listing.url}
              target={'_blank'}
              // @ts-expect-error
              style={{ 'text-decoration': 'none' }}
            >
              {listing.street}
            </Anchor>
          </Text>
        ) : (
          <Text fontType="bodySmall" marginLeft="xs">
            {listing.street}
          </Text>
        )}
      </S.CardAddress>
      <Text fontType="bodySmall" marginBottom="xs" marginLeft="xs">
        {listing.distance === '1.0' ? `${listing.distance} mile away` : `${listing.distance} miles away`}
      </Text>
      <Text fontType="bodySmall" marginBottom="xs" marginLeft="xs">
        <S.StatusDot isActive={isActive} />
        {listing.status}
      </Text>
      <S.Divider marginY="xs" />
      <Text fontType="bodySmall" marginBottom="xs" marginLeft="xs">
        {listingBeds}
      </Text>
      <Text fontType="bodySmall" marginBottom="xs" marginLeft="xs">
        {listingBaths}
      </Text>
      <Text fontType="bodySmall" marginBottom="xs" marginLeft="xs">
        {listingSqft}
      </Text>
      <S.Divider marginY="xs" />
      <Text fontType="bodySmall" marginBottom="xs" marginLeft="xs">
        {listingLaundryAmenity}
      </Text>
      <S.Divider marginY="xs" />
      <Text fontType="bodySmall" marginBottom="xs" marginLeft="xs">
        {listingParkingAmenity}
      </Text>
      <S.Divider marginY="xs" />
      <Text fontType="bodySmall" marginBottom="xs" marginLeft="xs">
        {listingAcAmenity}
      </Text>
      <S.Divider marginY="xs" />
      <Text fontType="bodySmall" marginBottom="xs" marginLeft="xs">
        {listingHeatingAmenity}
      </Text>
      <S.Divider marginY="xs" />
      <Text fontType="bodySmall" marginLeft="xs">
        {listingPetsAmenity}
      </Text>
      <S.Divider marginTop="xs" />
    </S.CardContainer>
  );
};

const CompsCarouselCard: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<CompsCarouselCardPropTypes>>
> = (props) => {
  if (!props.listing) {
    return null;
  }
  return <CompsCarouselCardComponent {...props} />;
};

export default CompsCarouselCard;
